import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { loadPdfJs } from '../lib/loadPdfJs';

// Baseline sits DELTA points above the box bottom, so text drawn at (x, y) lands inside the box.
// Must match the drawText baseline convention in backend fillTemplate().
const DELTA = 3;
const RENDER_WIDTH = 780;   // target CSS width per rendered page; scale derives from the PDF's own width

const SLOT_LABELS = {
  frontside_purchase:   'Frontside Purchase Contract',
  frontside_assignment: 'Frontside Assignment',
  backside_assignment:  'Backside Assignment',
};

const TYPE_COLORS = {
  fixed:      '#14504A',
  prefilled:  '#3B82F6',
  variable:   '#B85450',
  mls_metric: '#2E9E8F',
  deal_term:  '#C08A2D',
};

let keySeq = 1;
const nextKey = () => `f${keySeq++}`;

// px box (relative to the rendered page) ↔ PDF-point field coords (bottom-left origin).
function boxToField(px, scale, pageHeightPt) {
  return {
    x: px.left / scale,
    y: pageHeightPt - (px.top + px.height) / scale + DELTA,
    width: px.width / scale,
    height: px.height / scale,
  };
}
function fieldToBox(f, scale, pageHeightPt) {
  const height = f.height || 16;
  const width = f.width || 120;
  const boxTopPt = (f.y || 0) - DELTA + height;
  return {
    left: (f.x || 0) * scale,
    top: (pageHeightPt - boxTopPt) * scale,
    width: width * scale,
    height: height * scale,
  };
}

// WYSIWYG preview measurement (approximates pdf-lib's Helvetica with Arial). Used to show a fixed/
// prefilled field's value inside its box at the real font size — and to mirror the contract's
// shrink-to-fit so a too-long single-line value previews at the same shrunk size it will print at.
let _measureCtx = null;
function measureTextPx(text, fontPx) {
  if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d');
  _measureCtx.font = `${fontPx}px Helvetica, Arial, sans-serif`;
  return _measureCtx.measureText(String(text)).width;
}
// Count how many lines `text` wraps to at fontPx within boxWidthPx (mirrors backend wrapLines: honors
// explicit newlines, wraps on spaces).
function wrapCountLines(text, boxWidthPx, fontPx) {
  let count = 0;
  for (const para of String(text).split(/\r?\n/)) {
    if (para.trim() === '') { count++; continue; }
    let line = '';
    for (const w of para.trim().split(/\s+/)) {
      const test = line ? line + ' ' + w : w;
      if (line && measureTextPx(test, fontPx) > boxWidthPx) { count++; line = w; }
      else line = test;
    }
    count++;
  }
  return Math.max(1, count);
}
// Font size (px) for the WYSIWYG preview: wrap to the box width, shrink only if the wrapped block is
// taller than the box — mirrors backend drawMultiline.
function fitWrappedFontPx(text, boxWidthPx, boxHeightPx, baseFontPx) {
  let fs = baseFontPx;
  if (boxWidthPx > 0 && boxHeightPx > 0) {
    while (fs > 5 && wrapCountLines(text, boxWidthPx, fs) * fs * 1.25 > boxHeightPx) fs -= 0.5;
  }
  return fs;
}

export default function TemplateBuilder() {
  const { slot } = useParams();
  const navigate = useNavigate();

  const [templateId, setTemplateId] = useState(null);
  const [readOnly, setReadOnly]     = useState(false);
  const [scope, setScope]           = useState(null);
  const [status, setStatus]   = useState('loading');   // loading | no-template | ready | error
  const [errMsg, setErrMsg]   = useState(null);
  const [metrics, setMetrics] = useState({ mls_keys: [], settings_keys: [], field_types: [] });
  const [fields, setFields]   = useState([]);           // mapped fields (each has a client `key`)
  const [detected, setDetected] = useState([]);         // AcroForm widgets from the PDF (candidates)
  const [selectedKey, setSelectedKey] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [showDetected, setShowDetected] = useState(false);   // the auto-detected form-field list is opt-in
  const [pasteConfig, setPasteConfig] = useState(null);      // copied field config; next drawn box inherits it
  const [editingKey, setEditingKey] = useState(null);        // field whose config popup is open
  const [listOpen, setListOpen] = useState(true);            // right-sidebar full field list is collapsible
  const [drawMode, setDrawMode] = useState('text');          // what a new drawn box becomes: text | checkbox

  const pagesRef = useRef([]);        // [{ viewport, scale, pageHeightPt }]
  const containerRef = useRef(null);
  const scrollRef = useRef(null);     // the scrollable PDF column (for jump-to-field from the sidebar)
  const pasteRef = useRef(null);      // mirror of pasteConfig, read inside the (stable) draw handler
  const drawModeRef = useRef('text'); // mirror of drawMode, read inside the (stable) draw handler
  const chooseMode = (m) => { drawModeRef.current = m; setDrawMode(m); };

  // Copy a field's config so the next box(es) drawn inherit its label/type/binding — lets a value
  // that appears in several spots (address, price) be placed repeatedly without re-configuring.
  const startPaste = (f) => {
    const cfg = { label: f.label, field_type: f.field_type, mls_key: f.mls_key,
      fixed_value: f.fixed_value, default_value: f.default_value, settings_key: f.settings_key,
      element: f.element, check_rule: f.check_rule, format: f.format,
      term_key: f.term_key, term_mode: f.term_mode, multiline: f.multiline };
    pasteRef.current = cfg; setPasteConfig(cfg);
  };
  const stopPaste = () => { pasteRef.current = null; setPasteConfig(null); };

  // 1) Resolve the template row for this slot, load its metrics + saved fields.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetch('/api/contracts/templates', { credentials: 'include' }).then(r => r.json());
        const row = Array.isArray(list) ? list.find(t => t.slot === slot) : null;
        if (!row || !row.id || !row.exists) { if (!cancelled) setStatus('no-template'); return; }
        if (cancelled) return;
        setTemplateId(row.id);
        // Since migration 080 the slot may resolve to the ORG's template rather than this person's.
        // An agent may read that mapping but not change it — saving would 403 — so the builder opens
        // read-only and says why, rather than letting them lay out fields and lose the work on Save.
        setReadOnly(row.can_edit === false);
        setScope(row.scope || null);

        const [mx, fx] = await Promise.all([
          fetch('/api/contracts/template-metrics', { credentials: 'include' }).then(r => r.json()),
          fetch(`/api/contracts/templates/${row.id}/fields`, { credentials: 'include' }).then(r => r.json()),
        ]);
        if (cancelled) return;
        setMetrics(mx || { mls_keys: [], settings_keys: [], field_types: [] });
        setFields((fx.fields || []).map(f => ({
          key: nextKey(),
          kind: f.kind, acroform_name: f.acroform_name,
          page_index: f.page_index || 0,
          x: f.x, y: f.y, width: f.width, height: f.height, font_size: f.font_size || 10,
          label: f.label || '', field_type: f.field_type || 'variable',
          mls_key: f.mls_key || '', fixed_value: f.fixed_value || '',
          default_value: f.default_value || '', settings_key: f.settings_key || '',
          element: f.element || 'text', check_rule: f.check_rule || '', format: f.format || '',
          term_key: f.term_key || '', term_mode: f.term_mode || '', multiline: !!f.multiline,
        })));
        setStatus('ready');
      } catch (e) {
        if (!cancelled) { setStatus('error'); setErrMsg(e.message); }
      }
    })();
    return () => { cancelled = true; };
  }, [slot]);

  // 2) Once we have a templateId + ready, render the PDF pages and enumerate AcroForm widgets.
  useEffect(() => {
    if (status !== 'ready' || !templateId || !containerRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const loadingTask = pdfjs.getDocument({ url: `/api/contracts/templates/${templateId}/pdf`, withCredentials: true });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const container = containerRef.current;
        container.innerHTML = '';
        pagesRef.current = [];
        const widgets = [];

        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n);
          if (cancelled) return;
          const unscaled = page.getViewport({ scale: 1 });
          const scale = RENDER_WIDTH / unscaled.width;
          const viewport = page.getViewport({ scale });
          const pageHeightPt = unscaled.height;

          const pageWrap = document.createElement('div');
          pageWrap.style.cssText = `position:relative;margin:0 auto 14px;width:${viewport.width}px;height:${viewport.height}px;box-shadow:0 2px 10px rgba(0,0,0,0.25);`;
          pageWrap.dataset.pageIndex = String(n - 1);
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width; canvas.height = viewport.height;
          canvas.style.cssText = 'position:absolute;inset:0;';
          pageWrap.appendChild(canvas);
          container.appendChild(pageWrap);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;

          pagesRef.current[n - 1] = { scale, pageHeightPt, wrapEl: pageWrap };

          // AcroForm widgets on this page → candidates for one-click adoption.
          const anns = await page.getAnnotations();
          for (const a of anns) {
            if (a.subtype === 'Widget' && a.fieldName && Array.isArray(a.rect)) {
              const [x1, y1, x2, y2] = a.rect;
              widgets.push({
                acroform_name: a.fieldName, page_index: n - 1,
                x: Math.min(x1, x2), y: Math.min(y1, y2) + DELTA,
                width: Math.abs(x2 - x1), height: Math.abs(y2 - y1),
              });
            }
          }
        }
        if (!cancelled) setDetected(widgets);
      } catch (e) {
        if (!cancelled) { setStatus('error'); setErrMsg('Could not render the PDF: ' + e.message); }
      }
    })();
    return () => { cancelled = true; };
  }, [status, templateId]);

  const updateField = (key, patch) => setFields(fs => fs.map(f => f.key === key ? { ...f, ...patch } : f));
  const removeField = (key) => setFields(fs => fs.filter(f => f.key !== key));

  // Scroll the PDF column so a field's box is in view — used when a sidebar row is clicked.
  const scrollToField = (f) => {
    const cont = scrollRef.current;
    const p = pagesRef.current[f.page_index];
    if (!cont || !p || !p.wrapEl) return;
    const box = fieldToBox(f, p.scale, p.pageHeightPt);
    const contRect = cont.getBoundingClientRect();
    const wrapRect = p.wrapEl.getBoundingClientRect();
    const target = cont.scrollTop + (wrapRect.top - contRect.top) + box.top - 60;
    cont.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  };

  const adoptWidget = (w) => {
    // Don't add the same AcroForm field twice.
    if (fields.some(f => f.kind === 'acroform' && f.acroform_name === w.acroform_name)) return;
    const key = nextKey();
    setFields(fs => [...fs, {
      key, kind: 'acroform', acroform_name: w.acroform_name, page_index: w.page_index,
      x: w.x, y: w.y, width: w.width, height: w.height, font_size: 10,
      label: w.acroform_name, field_type: 'variable', mls_key: '', fixed_value: '', default_value: '', settings_key: '',
    }]);
    setSelectedKey(key);
  };

  // Drag-to-draw a new box on a page.
  const onPageMouseDown = useCallback((e) => {
    const pageWrap = e.target.closest('[data-page-index]');
    if (!pageWrap || e.button !== 0) return;
    const pageIndex = Number(pageWrap.dataset.pageIndex);
    const rect = pageWrap.getBoundingClientRect();
    const startX = e.clientX - rect.left, startY = e.clientY - rect.top;

    const ghost = document.createElement('div');
    ghost.style.cssText = `position:absolute;border:2px dashed ${TYPE_COLORS.variable};background:rgba(184,84,80,0.12);left:${startX}px;top:${startY}px;pointer-events:none;`;
    pageWrap.appendChild(ghost);

    const move = (ev) => {
      const cx = ev.clientX - rect.left, cy = ev.clientY - rect.top;
      ghost.style.left = Math.min(startX, cx) + 'px';
      ghost.style.top = Math.min(startY, cy) + 'px';
      ghost.style.width = Math.abs(cx - startX) + 'px';
      ghost.style.height = Math.abs(cy - startY) + 'px';
    };
    const up = (ev) => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      ghost.remove();
      const cx = ev.clientX - rect.left, cy = ev.clientY - rect.top;
      const px = { left: Math.min(startX, cx), top: Math.min(startY, cy), width: Math.abs(cx - startX), height: Math.abs(cy - startY) };
      if (px.width < 8 || px.height < 6) return;   // ignore tiny/accidental drags
      const p = pagesRef.current[pageIndex];
      if (!p) return;
      const coords = boxToField(px, p.scale, p.pageHeightPt);
      const key = nextKey();
      const cfg = pasteRef.current;   // if a field was copied, the new box inherits its config
      const mode = cfg ? (cfg.element || 'text') : drawModeRef.current;   // text or checkbox
      setFields(fs => [...fs, {
        key, kind: 'drawn', acroform_name: null, page_index: pageIndex,
        ...coords, font_size: Math.max(8, Math.min(16, Math.round(coords.height) - 4)),
        element: mode,
        label: cfg ? (cfg.label || '') : '',
        field_type: cfg ? cfg.field_type : 'variable',
        mls_key: cfg ? (cfg.mls_key || '') : '',
        fixed_value: cfg ? (cfg.fixed_value || '') : '',
        default_value: cfg ? (cfg.default_value || '') : '',
        settings_key: cfg ? (cfg.settings_key || '') : '',
        check_rule: mode === 'checkbox' ? (cfg ? (cfg.check_rule || 'always') : 'always') : '',
        format: cfg ? (cfg.format || '') : '',
        term_key: cfg ? (cfg.term_key || '') : '',
        term_mode: cfg ? (cfg.term_mode || '') : '',
        multiline: cfg ? !!cfg.multiline : false,
      }]);
      setSelectedKey(key);
      // Pop the config editor open for the new box so the user fills it right there — unless they're
      // rapidly placing copies (paste mode), where the config is already inherited.
      if (!cfg) setEditingKey(key);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }, []);

  const save = async () => {
    // Every field needs a label — otherwise it shows up nameless in the generate form.
    const unlabeled = fields.filter(f => !(f.label || '').trim());
    if (unlabeled.length) {
      setErrMsg(`Give every field a label before saving — ${unlabeled.length} still unlabeled.`);
      setSelectedKey(unlabeled[0].key);
      setEditingKey(unlabeled[0].key);   // open the first unlabeled field so they can name it
      return;
    }
    // Guardrail: cash-due and percent-earnest compute from the purchase price, so require a Purchase
    // price deal-term field (otherwise those fields generate blank on the contract).
    const hasPurchaseTerm = fields.some(f => f.field_type === 'deal_term' && f.term_key === 'purchase_price');
    const dependent = fields.find(f => f.field_type === 'deal_term' && (
      f.term_key === 'cash_at_closing' || (f.term_key === 'earnest_money' && f.term_mode === 'percent')));
    if (dependent && !hasPurchaseTerm) {
      setErrMsg(`Add a "Purchase price" deal-term field — ${dependent.term_key === 'cash_at_closing' ? 'Cash due at closing' : 'percent-based earnest money'} is computed from it.`);
      setSelectedKey(dependent.key);
      setEditingKey(dependent.key);
      return;
    }
    setSaving(true); setSaved(false); setErrMsg(null);
    try {
      const payload = {
        fields: fields.map(f => ({
          kind: f.kind, acroform_name: f.acroform_name,
          page_index: f.page_index, x: f.x, y: f.y, width: f.width, height: f.height, font_size: f.font_size,
          label: f.label, field_type: f.field_type,
          mls_key: f.mls_key || null, fixed_value: f.fixed_value || null,
          default_value: f.default_value || null, settings_key: f.settings_key || null,
          term_key: f.field_type === 'deal_term' ? (f.term_key || null) : null,
          term_mode: f.field_type === 'deal_term' ? (f.term_mode || null) : null,
          element: f.element || 'text', check_rule: f.element === 'checkbox' ? (f.check_rule || 'always') : null,
          format: f.format || null,
        })),
      };
      const r = await fetch(`/api/contracts/templates/${templateId}/fields`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Save failed');
      setSaved(true);
    } catch (e) { setErrMsg(e.message); } finally { setSaving(false); }
  };

  const slotName = SLOT_LABELS[slot] || slot;
  // On the assignment slots, the MLS-field dropdown also offers assignment values (assignee, fee,
  // dates…) so those templates map like the purchase contract.
  const isAssignmentSlot = slot === 'frontside_assignment' || slot === 'backside_assignment';
  const builderMetrics = { ...metrics, mls_keys: isAssignmentSlot ? [...(metrics.assignment_keys || []), ...(metrics.mls_keys || [])] : metrics.mls_keys };
  // Detected AcroForm widgets, minus ones already adopted and minus the auto-generated junk names
  // (many fillable PDFs like TREC name unlabeled blanks "undefined", "undefined_3", etc.) — those are
  // undecodable, so we hide them; the user draws a box over that spot instead.
  const JUNK = /^\s*undefined(_\d+)?\s*$/i;
  const unmappedWidgets = detected.filter(w =>
    w.acroform_name && !JUNK.test(w.acroform_name) &&
    !fields.some(f => f.kind === 'acroform' && f.acroform_name === w.acroform_name));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 40px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <button onClick={() => navigate('/settings')} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 6 }}>← Settings</button>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em' }}>Map fields · {slotName}</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 13, marginTop: 4 }}>Drag on the PDF to add a text box, or adopt an existing form field. Label each and choose how it fills.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {saved && <span style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>Saved ✓</span>}
          {errMsg && <span style={{ fontSize: 12, color: '#B85450', maxWidth: 260 }}>{errMsg}</span>}
          <button onClick={save} disabled={saving || readOnly || status !== 'ready'}
            title={readOnly ? 'This is your organization\u2019s template — upload your own to map fields' : undefined}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: readOnly ? 'var(--navy-border)' : 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (saving || readOnly) ? 'default' : 'pointer', opacity: readOnly ? 0.6 : 1 }}>
            {saving ? 'Saving…' : `Save ${fields.length} field${fields.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      {/* Read-only: the resolved template is the organization's and this caller is not owner/admin. */}
      {readOnly && status === 'ready' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '9px 14px', borderRadius: 8, background: 'rgba(176,141,87,0.10)', border: '1px solid rgba(176,141,87,0.45)' }}>
          <span style={{ fontSize: 12, color: 'var(--gray-300)' }}>
            This is your <strong>organization&rsquo;s</strong> template, shown read-only — its field mapping is shared by everyone who has not uploaded their own.
            To map your own fields, upload your contract to this slot in <span role="button" tabIndex={0} onClick={() => navigate('/settings')} onKeyDown={e => { if (e.key === 'Enter') navigate('/settings'); }} style={{ color: 'var(--teal)', cursor: 'pointer', textDecoration: 'underline' }}>Settings</span>.
          </span>
        </div>
      )}
      {scope === 'mine' && status === 'ready' && !readOnly && (
        <div style={{ marginBottom: 12, fontSize: 11.5, color: 'var(--gray-500)' }}>
          Mapping <strong style={{ color: 'var(--teal)' }}>your own</strong> template for this slot. Only your contracts use it.
        </div>
      )}

      {pasteConfig && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, padding: '8px 14px', borderRadius: 8, background: 'rgba(46,158,143,0.10)', border: '1px solid var(--teal)' }}>
          <span style={{ fontSize: 12, color: 'var(--white)' }}>
            Placing copies of <strong>{pasteConfig.label || 'this field'}</strong> — draw a box on the PDF for each spot it should appear. They all fill together.
          </span>
          <button onClick={stopPaste} style={{ fontSize: 12, fontWeight: 700, background: 'var(--teal)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>Done</button>
        </div>
      )}

      {status === 'loading' && <div style={{ color: 'var(--gray-500)', fontSize: 14 }}>Loading template…</div>}
      {status === 'error' && <div style={{ color: '#B85450', fontSize: 14 }}>{errMsg || 'Something went wrong.'}</div>}
      {status === 'no-template' && (
        <div style={{ color: 'var(--gray-300)', fontSize: 14 }}>
          No PDF uploaded to this slot yet. Go to <a onClick={() => navigate('/settings')} style={{ color: 'var(--teal)', cursor: 'pointer' }}>Settings → Contract Templates</a> and upload one first.
        </div>
      )}

      {status === 'ready' && (
        <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
          {/* PDF canvas column */}
          <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', background: 'var(--navy)', borderRadius: 10, padding: 16 }}>
            <div ref={containerRef} onMouseDown={onPageMouseDown} style={{ cursor: 'crosshair' }} />
            {/* Field overlays are rendered via React on top of each page. Detected-field outlines only
                show when the user opts in, so the PDF isn't buried under hundreds of boxes. */}
            <FieldOverlays fields={fields} detected={showDetected ? unmappedWidgets : []} pagesRef={pagesRef} selectedKey={selectedKey}
              onSelect={(k) => { setSelectedKey(k); setEditingKey(k); }} onChange={updateField} onAdopt={adoptWidget} />
          </div>

          {/* Field editor panel */}
          <div style={{ width: 340, flexShrink: 0, overflow: 'auto', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 16 }}>
            {/* Primary flow: draw boxes. The detected-form-field list is tucked behind a toggle. */}
            <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 8, background: 'var(--navy)', border: '1px solid var(--navy-border)' }}>
              <div style={{ fontSize: 12, color: 'var(--gray-300)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--white)' }}>Drag a box</strong> over each blank the contract needs filled, then label it and pick how it fills.
              </div>
              {/* What a drawn box becomes: a text field or a checkbox. */}
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {[['text', 'Text field'], ['checkbox', 'Checkbox']].map(([m, lbl]) => (
                  <button key={m} onClick={() => chooseMode(m)}
                    style={{ flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${drawMode === m ? 'var(--teal)' : 'var(--navy-border)'}`,
                      background: drawMode === m ? 'rgba(46,158,143,0.12)' : 'transparent',
                      color: drawMode === m ? 'var(--teal)' : 'var(--gray-300)' }}>
                    {lbl}
                  </button>
                ))}
              </div>
              {drawMode === 'checkbox' && (
                <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Boxes you draw now become checkboxes auto-checked by a rule (e.g. built before 1978).</div>
              )}
              {unmappedWidgets.length > 0 && (
                <button onClick={() => setShowDetected(s => !s)}
                  style={{ marginTop: 8, fontSize: 11, background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                  {showDetected ? 'Hide' : 'Show'} {unmappedWidgets.length} detected form field{unmappedWidgets.length === 1 ? '' : 's'} (advanced)
                </button>
              )}
            </div>

            {showDetected && unmappedWidgets.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 8 }}>Click to snap onto an existing PDF field instead of drawing:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {unmappedWidgets.map((w, i) => (
                    <button key={i} onClick={() => adoptWidget(w)} title="Add this form field"
                      style={{ fontSize: 11, padding: '4px 9px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--gray-300)', cursor: 'pointer', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      + {w.acroform_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div onClick={() => setListOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, userSelect: 'none' }}>
              <span>All mapped fields ({fields.length})</span>
              <span style={{ fontSize: 12 }}>{listOpen ? '▾' : '▸'}</span>
            </div>
            {listOpen && (
              <>
                {fields.length === 0 && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>None yet. Drag a box on the PDF over a blank to add one.</div>}
                {/* Ordered as they appear in the contract: by page, then top-to-bottom (higher box top
                    on the page first). Clicking a row jumps the PDF to that field. */}
                {[...fields]
                  .sort((a, b) => (a.page_index - b.page_index)
                    || (((b.y || 0) + (b.height || 0)) - ((a.y || 0) + (a.height || 0))))
                  .map(f => (
                  <FieldEditor key={f.key} f={f} metrics={builderMetrics} selected={selectedKey === f.key}
                    onSelect={() => { setSelectedKey(f.key); scrollToField(f); }} onChange={patch => updateField(f.key, patch)}
                    onRemove={() => removeField(f.key)} onCopy={() => startPaste(f)} />
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* New-box / edit popup — fill a field's config without scrolling the sidebar. */}
      {editingKey && (() => {
        const ef = fields.find(f => f.key === editingKey);
        if (!ef) return null;
        return (
          <div onClick={() => setEditingKey(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(8,12,20,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ width: 360, maxHeight: '85vh', overflowY: 'auto', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>Configure field</span>
                <button onClick={() => setEditingKey(null)}
                  style={{ fontSize: 12, fontWeight: 700, background: 'var(--teal)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 14px', cursor: 'pointer' }}>Done</button>
              </div>
              <FieldEditor f={ef} metrics={builderMetrics} selected autoFocus onSelect={() => {}}
                onChange={patch => updateField(ef.key, patch)}
                onRemove={() => { removeField(ef.key); setEditingKey(null); }}
                onCopy={() => { startPaste(ef); setEditingKey(null); }} />
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Absolutely-positioned overlay boxes drawn over the pdf.js canvases. Recomputed on every render from
// the page viewports so they track the rendered geometry.
function FieldOverlays({ fields, detected, pagesRef, selectedKey, onSelect, onChange, onAdopt }) {
  const [, force] = useState(0);
  // The pages render asynchronously; nudge a re-render shortly after mount so overlays appear once
  // pagesRef is populated.
  useEffect(() => { const t = setTimeout(() => force(x => x + 1), 300); return () => clearTimeout(t); });

  return (
    <>
      {detected.map((w, i) => {
        const p = pagesRef.current[w.page_index];
        if (!p || !p.wrapEl) return null;
        const box = fieldToBox(w, p.scale, p.pageHeightPt);
        return createPortalBox(p.wrapEl, `det${i}`, box, {
          border: '1.5px dashed var(--gray-500)', background: 'rgba(120,120,120,0.10)', cursor: 'pointer',
        }, () => onAdopt(w), null);
      })}
      {fields.map(f => {
        const p = pagesRef.current[f.page_index];
        if (!p || !p.wrapEl) return null;
        const box = fieldToBox(f, p.scale, p.pageHeightPt);
        const color = f.element === 'checkbox' ? '#9333EA' : (TYPE_COLORS[f.field_type] || TYPE_COLORS.variable);
        const sel = selectedKey === f.key;
        // WYSIWYG: a Fixed / Pre-filled field with a literal value previews that value in its box, at
        // the field's font size (scaled), mirroring how it will print — including shrink-to-fit for a
        // too-long single line, or wrapping when Multi-line is on.
        const previewText = f.element === 'checkbox' ? ''
          : f.field_type === 'fixed' ? (f.fixed_value || '')
          : f.field_type === 'prefilled' ? (f.default_value || '')
          : f.field_type === 'deal_term' ? (f.default_value || '')
          : '';
        const preview = previewText ? {
          text: previewText, baseFontPx: (Number(f.font_size) || 10) * p.scale,
        } : null;
        // Draggable + resizable: onGeom converts the new pixel box back to PDF field coords.
        const geom = { scale: p.scale, pageHeightPt: p.pageHeightPt, selected: sel, onGeom: (patch) => onChange(f.key, patch) };
        return createPortalBox(p.wrapEl, f.key, box, {
          border: `2px solid ${color}`, background: color + '22',
          outline: sel ? `2px solid ${color}` : 'none', outlineOffset: 2,
        }, () => onSelect(f.key), f.label || (f.element === 'checkbox' ? '☑ ' + (f.check_rule || 'checkbox') : (f.field_type === 'mls_metric' ? f.mls_key : '')), preview, geom);
      })}
    </>
  );
}

// Render a positioned box into a page wrapper via a React portal (the page wrappers are created
// imperatively by pdf.js rendering, so we portal overlays onto them).
function createPortalBox(wrapEl, key, box, style, onClick, label, preview, geom) {
  return (
    <PortalBox key={key} wrapEl={wrapEl} box={box} style={style} onClick={onClick} label={label} preview={preview} geom={geom} />
  );
}

// Resize-handle style for a given direction (n/s/e/w/ne/nw/se/sw).
function handleStyle(dir) {
  const s = 9;
  const base = { position: 'absolute', width: s, height: s, background: '#fff', border: '1.5px solid #2E9E8F', borderRadius: 2, zIndex: 3 };
  const pos = {};
  if (dir.includes('n')) pos.top = -s / 2; if (dir.includes('s')) pos.bottom = -s / 2;
  if (dir.includes('w')) pos.left = -s / 2; if (dir.includes('e')) pos.right = -s / 2;
  if (dir === 'n' || dir === 's') { pos.left = '50%'; pos.marginLeft = -s / 2; }
  if (dir === 'e' || dir === 'w') { pos.top = '50%'; pos.marginTop = -s / 2; }
  const cursor = { n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize', ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize' }[dir];
  return { ...base, ...pos, cursor };
}

function PortalBox({ wrapEl, box, style, onClick, label, preview, geom }) {
  const draggable = !!geom;
  // Move (mode 'move') or resize (a compass direction). Converts the new pixel box back to PDF coords
  // via boxToField on every mousemove. A mousedown with no movement is treated as a plain click/select.
  const startInteract = (e, mode) => {
    if (!draggable) return;
    e.stopPropagation(); e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const orig = { left: box.left, top: box.top, width: box.width, height: box.height };
    let moved = false;
    const move = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 3) return;
      moved = true;
      let { left, top, width, height } = orig;
      if (mode === 'move') { left += dx; top += dy; }
      else {
        if (mode.includes('e')) width = Math.max(6, orig.width + dx);
        if (mode.includes('s')) height = Math.max(5, orig.height + dy);
        if (mode.includes('w')) { width = Math.max(6, orig.width - dx); left = orig.left + orig.width - width; }
        if (mode.includes('n')) { height = Math.max(5, orig.height - dy); top = orig.top + orig.height - height; }
      }
      geom.onGeom(boxToField({ left, top, width, height }, geom.scale, geom.pageHeightPt));
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      if (!moved && mode === 'move' && onClick) onClick();   // no drag → select
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };
  // Preview text always wraps to the box width and shrinks only if the box is too short — mirrors the
  // generated contract.
  let previewNode = null;
  if (preview && preview.text) {
    const fontPx = fitWrappedFontPx(preview.text, box.width - 2, box.height, preview.baseFontPx);
    previewNode = (
      <div style={{
        position: 'absolute', inset: 0, padding: '0 1px', display: 'flex', alignItems: 'flex-start',
        fontFamily: 'Helvetica, Arial, sans-serif', fontSize: fontPx, lineHeight: 1.25, color: '#111',
        overflow: 'hidden', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        pointerEvents: 'none',
      }}>{preview.text}</div>
    );
  }
  const onBodyDown = draggable ? (e => startInteract(e, 'move')) : (e => { e.stopPropagation(); onClick && onClick(); });
  return createPortal(
    <div onMouseDown={onBodyDown}
      style={{ position: 'absolute', left: box.left, top: box.top, width: box.width, height: box.height, boxSizing: 'border-box', ...style, cursor: draggable ? 'move' : (style.cursor || 'pointer') }}>
      {previewNode}
      {geom && geom.selected && ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map(dir => (
        <div key={dir} onMouseDown={e => startInteract(e, dir)} style={handleStyle(dir)} />
      ))}
      {label ? <span style={{ position: 'absolute', top: -15, left: 0, fontSize: 9, color: 'var(--white)', background: 'rgba(0,0,0,0.6)', padding: '0 3px', borderRadius: 3, whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none' }}>{label}</span> : null}
    </div>,
    wrapEl
  );
}

function FieldEditor({ f, metrics, selected, onSelect, onChange, onRemove, onCopy, autoFocus }) {
  const color = f.element === 'checkbox' ? '#9333EA' : (TYPE_COLORS[f.field_type] || TYPE_COLORS.variable);
  const input = { width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, marginTop: 4 };
  const lbl = { fontSize: 10, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' };
  return (
    <div onClick={onSelect} style={{ border: `1px solid ${selected ? color : 'var(--navy-border)'}`, borderLeft: `3px solid ${color}`, borderRadius: 8, padding: 10, marginBottom: 8, background: 'var(--navy)', cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{f.element === 'checkbox' ? 'checkbox' : (f.kind === 'acroform' ? 'form field' : 'drawn box')} · pg {f.page_index + 1}</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={e => { e.stopPropagation(); onCopy(); }} title="Copy this field, then draw boxes to place it elsewhere"
            style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 11, cursor: 'pointer', padding: 0 }}>Copy to spots</button>
          <button onClick={e => { e.stopPropagation(); onRemove(); }} style={{ background: 'none', border: 'none', color: '#B85450', fontSize: 11, cursor: 'pointer', padding: 0 }}>Remove</button>
        </div>
      </div>
      <input style={input} placeholder={f.element === 'checkbox' ? 'Checkbox label (e.g. Lead paint disclosure)' : 'Field label (e.g. Purchase price)'} value={f.label} autoFocus={autoFocus} onChange={e => onChange({ label: e.target.value })} />

      {f.element === 'checkbox' ? (
        <div style={{ marginTop: 8 }}>
          <div style={lbl}>Check this box when</div>
          <select style={input} value={f.check_rule || 'always'} onChange={e => onChange({ check_rule: e.target.value })}>
            {(metrics.check_rules || []).map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <div style={{ marginTop: 8 }}>
            <div style={lbl}>Stamp (blank = ✕)</div>
            <input style={input} placeholder="e.g. initials — leave blank for ✕" value={f.fixed_value || ''} onChange={e => onChange({ fixed_value: e.target.value })} />
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={lbl}>…or stamp a workspace default</div>
            <select style={input} value={f.settings_key || ''} onChange={e => onChange({ settings_key: e.target.value })}>
              <option value="">— none —</option>
              {(metrics.settings_keys || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Auto-stamped at generation — e.g. lead-paint initials by year built. No manual entry needed.</div>
        </div>
      ) : (
      <>
      <div style={{ marginTop: 8 }}>
        <div style={lbl}>Type</div>
        <select style={input} value={f.field_type} onChange={e => onChange({ field_type: e.target.value })}>
          {(metrics.field_types || []).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      {f.field_type === 'mls_metric' && (
        <div style={{ marginTop: 8 }}>
          <div style={lbl}>MLS field</div>
          <select style={input} value={f.mls_key} onChange={e => onChange({ mls_key: e.target.value })}>
            <option value="">— choose —</option>
            {(metrics.mls_keys || []).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
      )}
      {f.field_type === 'deal_term' && (
        <>
          <div style={{ marginTop: 8 }}>
            <div style={lbl}>Deal term</div>
            <select style={input} value={f.term_key || ''} onChange={e => onChange({ term_key: e.target.value })}>
              <option value="">— choose —</option>
              {(metrics.deal_term_keys || []).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          {(f.term_key === 'close_date' || f.term_key === 'acceptance_deadline') && (
            <div style={{ marginTop: 8 }}>
              <div style={lbl}>Default {f.term_key === 'close_date' ? 'days from contract date' : 'hours to accept'}</div>
              <input style={input} type="number" placeholder={f.term_key === 'close_date' ? '14' : '48'}
                value={f.default_value || ''} onChange={e => onChange({ default_value: e.target.value })} />
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Prints the computed date; editable per contract at generation.</div>
            </div>
          )}
          {(f.term_key === 'earnest_money') && (
            <>
              <div style={{ marginTop: 8 }}>
                <div style={lbl}>How it's set</div>
                <select style={input} value={f.term_mode || 'preset'} onChange={e => onChange({ term_mode: e.target.value })}>
                  <option value="preset">Preset amount (editable each time)</option>
                  <option value="percent">Percent of purchase price (auto)</option>
                </select>
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={lbl}>{f.term_mode === 'percent' ? 'Percent of purchase price' : 'Preset amount ($)'}</div>
                <input style={input} type="number" placeholder={f.term_mode === 'percent' ? '1' : '1000'}
                  value={f.default_value || ''} onChange={e => onChange({ default_value: e.target.value })} />
                <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>
                  {f.term_mode === 'percent'
                    ? 'e.g. 1 → 1% of the purchase price (auto-computed).'
                    : 'Prefilled on every contract; editable at generation.'}
                </div>
              </div>
            </>
          )}
          {(f.term_key === 'offer_date') && (
            <div style={{ marginTop: 8 }}>
              <div style={lbl}>Date style</div>
              <select style={input} value={f.term_mode || 'long'} onChange={e => onChange({ term_mode: e.target.value })}>
                <option value="long">September 21, 2026</option>
                <option value="numeric">09/21/2026</option>
              </select>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Fills in automatically with the date the offer is drafted. The agent can pick a different date at generation.</div>
            </div>
          )}
          {(f.term_key === 'cash_at_closing') && (
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Computed automatically = purchase price − earnest money.</div>
          )}
        </>
      )}
      {f.field_type === 'fixed' && (
        <>
          <div style={{ marginTop: 8 }}>
            <div style={lbl}>Fixed value</div>
            <input style={input} placeholder="Same on every contract" value={f.fixed_value} onChange={e => onChange({ fixed_value: e.target.value })} />
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={lbl}>…or bind to a workspace default</div>
            <select style={input} value={f.settings_key} onChange={e => onChange({ settings_key: e.target.value })}>
              <option value="">— none —</option>
              {(metrics.settings_keys || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </>
      )}
      {f.field_type === 'prefilled' && (
        <div style={{ marginTop: 8 }}>
          <div style={lbl}>Default (editable at generation)</div>
          <input style={input} placeholder="e.g. 7 days" value={f.default_value} onChange={e => onChange({ default_value: e.target.value })} />
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <div style={lbl}>Format</div>
        <select style={input} value={f.format || ''} onChange={e => onChange({ format: e.target.value })}>
          <option value="">Plain text</option>
          <option value="currency">Currency (1,000s separators)</option>
        </select>
      </div>
      {/* Font size is editable inline from the sidebar list — stop the click from bubbling to the
          card's onClick (which would open the Configure-field popup). */}
      <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
        <div style={lbl}>Font size (pt)</div>
        <input style={input} type="number" min="5" max="24" placeholder="10"
          value={f.font_size ?? ''} onChange={e => onChange({ font_size: e.target.value === '' ? '' : Number(e.target.value) })} />
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Set the same size on related fields to keep them uniform. Text wraps to the next line automatically, and shrinks only if the box isn't tall enough.</div>
      </div>
      </>
      )}
    </div>
  );
}
